import { db, type PendingPhoto } from './db/schema.js';
import { uuidv4 } from './uuid.js';
import { api, HttpError } from './api.js';
import { queryClient } from './queryClient.js';
import { queueOwnerId, requireQueueOwner, belongsToCurrentUser } from './queue-owner.js';

let inFlight = false;

export async function enqueuePhoto(input: {
  orderId: string; roomId?: string | null; paymentClaimId?: string | null; kind: PendingPhoto['kind']; blob: Blob;
}): Promise<string> {
  const ownerId = requireQueueOwner();
  const id = uuidv4();
  await db.photos.add({
    ...input, id, ownerId, roomId: input.roomId ?? null,
    uploadedUrl: null, createdAt: Date.now(), status: 'pending', lastError: null,
  });
  if (navigator.onLine) void flushPhotos();
  return id;
}

/** Атомарный захват + аренда позволяют продолжить загрузку после закрытия PWA. */
export async function flushPhotos(): Promise<void> {
  const ownerId = queueOwnerId();
  if (inFlight || !navigator.onLine || !ownerId) return;
  inFlight = true;
  const touched = new Set<string>();
  try {
    while (navigator.onLine && queueOwnerId() === ownerId) {
      const next = await db.transaction('rw', db.photos, async () => {
        const candidate = await db.photos.where('ownerId').equals(ownerId)
          .filter((item) => item.status === 'pending' || (item.status === 'uploading' && (item.leaseUntil ?? 0) <= Date.now()))
          .sortBy('createdAt').then((items) => items[0]);
        if (!candidate) return undefined;
        await db.photos.update(candidate.id, { status: 'uploading', leaseUntil: Date.now() + 120_000 });
        return candidate;
      });
      if (!next) break;
      try {
        const body = new FormData();
        body.append('file', next.blob, `${next.id}.jpg`);
        body.append('kind', next.kind);
        if (next.roomId) body.append('roomId', next.roomId);
        if (next.paymentClaimId) body.append('paymentClaimId', next.paymentClaimId);
        await api(`/v1/orders/${next.orderId}/photos`, {
          method: 'POST', body, idempotencyKey: next.id, expectedUserId: ownerId,
        });
        await db.photos.delete(next.id);
        touched.add(next.orderId);
      } catch (error) {
        const permanent = error instanceof HttpError && error.status >= 400 && error.status < 500
          && ![401, 408, 429].includes(error.status);
        await db.photos.update(next.id, {
          status: permanent ? 'failed' : 'pending', lastError: (error as Error).message,
        });
        if (!permanent) break;
      }
    }
  } finally {
    inFlight = false;
    for (const orderId of touched) void queryClient.invalidateQueries({ queryKey: ['order', orderId] });
  }
}

export async function dropPhoto(id: string): Promise<void> {
  const photo = await db.photos.get(id);
  if (photo && belongsToCurrentUser(photo)) await db.photos.delete(id);
}
